import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { inject, observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';

import Scene from '../Scene';
import Container from '../../components/Container';
import Indent from '../../components/Indent';
import { crossScreenMarginBottom, link, toVw, toVwMobile, toVwTablet } from '../../utils/styles';
import Intro from './components/Intro';
import BigText from '../../components/BigText';
import Awards from './components/Awards';
import ContentItems from '../../components/ContentItems/';
import mq from '@/utils/media-queries';
import withBackLink from '../../components/withBackLink';
import PreviewNextCase from '../../components/PreviewNextCase';
import NotFound from '../NotFound';
import AnimationOnPageLoadItem from '../../components/AnimationOnPageLoadItem';
import api from '../../utils/api';
import responseIs404 from '../../utils/responseIs404';

const CaseScene = styled(Scene)`
  padding-top: 0;
`;

const CONTENT_PADDING_TOP = 144;

const Content = styled.div`
  padding-top: ${toVw(CONTENT_PADDING_TOP)};
  padding-bottom: ${toVw(254)};

  ${mq.tablet(css`
    padding-top: ${toVwTablet(CONTENT_PADDING_TOP)};
    padding-bottom: ${toVwTablet(213)};
  `)};

  ${mq.mobile(css`
    padding-top: ${toVwMobile(CONTENT_PADDING_TOP)};
    padding-bottom: ${toVwMobile(299)};
  `)};
`;

const Header = styled.header`
  ${crossScreenMarginBottom(144)};
`;

const TITLE_MARGIN_BOTTOM = 46;

const TitleWrapper = styled.div`
  margin-bottom: ${toVw(TITLE_MARGIN_BOTTOM)};

  ${mq.tablet(css`
    margin-bottom: ${toVwTablet(TITLE_MARGIN_BOTTOM)};
  `)};

  ${mq.mobile(css`
    margin-bottom: ${toVwMobile(54)};
  `)};
`;

const Title = BigText.withComponent('h1');

const Description = styled.div`
  margin-bottom: ${toVw(72)};

  &:last-child {
    margin-bottom: 0;
  }

  ${mq.tablet(css`
    margin-bottom: ${toVwTablet(78)};
  `)};

  ${mq.mobile(css`
    margin-bottom: ${toVwMobile(44)};
  `)};
`;

const StyledAwards = styled(Awards)`
  ${crossScreenMarginBottom(18)};
`;

const ExternalLink = styled.a`
  ${link};
`;

const StyledContentItems = styled(ContentItems)`
  ${crossScreenMarginBottom(144)};
`;

const TitleWrapperBottom = styled.div`
  ${crossScreenMarginBottom(118)};
`;

const Cols = styled.div`
  display: flex;

  ${mq.mobile(css`
    display: block;
  `)};
`;

const ResultCols = Cols.extend`
  ${crossScreenMarginBottom(80)};
`;

const ResultText = styled.div`
  width: ${toVw(540)};

  ${mq.tablet(css`
    width: ${toVwTablet(468)};
  `)};

  ${mq.tablet(css`
    width: auto;
  `)};
`;

const TeamCols = Cols.extend`
  ${crossScreenMarginBottom(80)};
`;

const Col = styled.div`
  flex-shrink: 0;

  &:first-child {
    flex-basis: ${toVw(216)};
  }

  ${mq.tablet(css`
    &:first-child {
      flex-basis: ${toVwTablet(208)};
    }
  `)};

  ${mq.mobile(css`
    &:first-child {
      width: 100%;
      margin-bottom: ${toVwMobile(40)};
    }
  `)};
`;

const ColsName = styled.div`
  color: ${props => props.theme.colorMinor};
`;

const TeamMember = styled.div`
  display: flex;
`;

const TeamMemberInfo = styled.div``;

const TEAM_MEMBER_POST_WIDTH = 324;

const TeamMemberPost = TeamMemberInfo.extend`
  width: ${toVw(TEAM_MEMBER_POST_WIDTH)};

  ${mq.tablet(css`
    width: ${toVwTablet(TEAM_MEMBER_POST_WIDTH)};
  `)};

  ${mq.mobile(css`
    width: ${toVwMobile(280)};
  `)};
`;

class Case extends React.Component {
  static propTypes = {
    slug: PropTypes.string,
    match: PropTypes.object,
    shouldHandleColors: PropTypes.bool,
  };

  state = {
    entity: {},
    animationEnded: false,
    nextAnimationRunning: false,
  };

  componentDidMount() {
    this.load();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.slug !== this.props.slug ||
      (prevProps.match.params && prevProps.match.params.slug !== this.props.match.params.slug)
    ) {
      this.load();
    }
  }

  load = async () => {
    const slug = this.props.slug || this.props.match.params.slug;
    if (!slug) {
      return;
    }

    const response = await api.case(slug);
    const data = await response.json();
    if (responseIs404(response)) {
      this.setState({ notFound: true });
      return;
    }
    this.setState({ entity: { ...data } });
  };

  render() {
    if (this.state.notFound) {
      return <NotFound />;
    }

    const {
      intro,
      title,
      subtitle,
      description,
      awards,
      link,
      content,
      result,
      team,
      isDarkTheme,
      nextPreview,
    } = this.state.entity;

    const externalLink = link && link.url && <ExternalLink href={link.url}>{link.name || link.url}</ExternalLink>;

    return (
      <CaseScene
        data-test="case"
        dark={isDarkTheme}
        {...this.props}
        onAnimationEnded={() =>
          this.setState({
            animationEnded: true,
          })
        }
        brightFooter={nextPreview && nextPreview.isDarkTheme}
      >
        {intro && (
          <Intro {...intro} autoPlay={this.state.animationEnded} animationRunning={this.state.nextAnimationRunning} />
        )}
        <Content>
          <Container>
            <Indent>
              <Header>
                <TitleWrapper data-hide-on-animation>
                  <Title data-test="case-title">{title}</Title>
                  {subtitle && <BigText>{subtitle}</BigText>}
                </TitleWrapper>
                {/* {description && <Description>{description}</Description>} */}
                <Description dangerouslySetInnerHTML={{ __html: description }} />
                {awards && awards.length > 0 && <StyledAwards items={awards} />}
                {externalLink}
              </Header>
            </Indent>
            {content && <StyledContentItems items={content} />}
            <Indent>
              <TitleWrapperBottom>
                <Title>{title}</Title>
                {subtitle && <BigText>{subtitle}</BigText>}
              </TitleWrapperBottom>
              {result && (
                <ResultCols>
                  <Col>
                    <ColsName>Work done</ColsName>
                  </Col>
                  <Col>
                    <ResultText>{result}</ResultText>
                  </Col>
                </ResultCols>
              )}
              {team &&
                team.length > 0 && (
                  <TeamCols>
                    <Col>
                      <ColsName>Team</ColsName>
                    </Col>
                    <Col>
                      {team.map((member, index) => (
                        <TeamMember key={index}>
                          <TeamMemberPost>{member.post}</TeamMemberPost>
                          <TeamMemberInfo>{member.name}</TeamMemberInfo>
                        </TeamMember>
                      ))}
                    </Col>
                  </TeamCols>
                )}
              {awards && awards.length > 0 && <StyledAwards items={awards} />}
              {externalLink}
            </Indent>
          </Container>
        </Content>
        {nextPreview &&
          nextPreview.to && (
            <PreviewNextCase {...nextPreview} onAnimationStart={() => this.setState({ nextAnimationRunning: true })} />
          )}
      </CaseScene>
    );
  }
}

const CaseWithBackLink = withBackLink('cases')(observer(Case));

export default withRouter(
  inject(stores => ({
    logoStore: stores.store.logoStore,
  }))(CaseWithBackLink)
);



// WEBPACK FOOTER //
// ./src/scenes/Case/index.js